import { Link, useLocation } from "react-router-dom"
import { Waves } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import { ROUTES, APP_NAME } from "@/shared/lib/constants"

/**
 * Extract farmId from current URL path if on a farm-scoped route
 */
function useFarmIdFromUrl(): string | null {
  const location = useLocation()
  const match = location.pathname.match(/^\/farms\/([^/]+)/)
  return match ? match[1] : null
}

// Navigation items with optional farmFeature for farm-scoped routes
const navigation = [
  {
    name: "Dashboard",
    href: ROUTES.HOME,
    farmFeature: null, // Not farm-scoped
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    name: "Farms",
    href: ROUTES.FARMS,
    farmFeature: null, // Not farm-scoped
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
  },
  {
    name: "Ponds",
    href: ROUTES.PONDS,
    farmFeature: "ponds", // Farm-scoped: /farms/:farmId/ponds
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
        />
      </svg>
    ),
  },
  {
    name: "Water Quality",
    href: ROUTES.WATER_QUALITY,
    farmFeature: "water-quality", // Farm-scoped: /farms/:farmId/water-quality
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    name: "Fish Health",
    href: ROUTES.FISH_HEALTH,
    farmFeature: "fish-health", // Farm-scoped: /farms/:farmId/fish-health
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
    ),
  },
  {
    name: "Maintenance",
    href: ROUTES.MAINTENANCE,
    farmFeature: "maintenance", // Farm-scoped: /farms/:farmId/maintenance
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
  {
    name: "Feed",
    href: ROUTES.FEED,
    farmFeature: "feed", // Farm-scoped: /farms/:farmId/feed
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    name: "AI Insights",
    href: ROUTES.AI,
    farmFeature: "ai", // Farm-scoped: /farms/:farmId/ai
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
  },
  {
    name: "Team",
    href: ROUTES.TEAM,
    farmFeature: null, // Not farm-scoped
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
  },
  {
    name: "Settings",
    href: ROUTES.SETTINGS,
    farmFeature: null, // Not farm-scoped
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
]

export function Sidebar() {
  // Get farmId from current URL to use for farm-scoped routes
  const currentFarmId = useFarmIdFromUrl()

  return (
    <div className="flex h-full w-[280px] flex-col bg-gradient-to-b from-slate-950 to-slate-900">
      {/* Logo - Premium Brand Area */}
      <div className="flex h-[72px] items-center gap-3 px-6 bg-slate-900/50">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/20">
          <Waves className="h-6 w-6 text-white" strokeWidth={1} />
        </div>
        <div className="flex flex-col">
          <span className="font-roboto text-[17px] font-semibold text-white tracking-tight leading-tight">
            {APP_NAME}
          </span>
          <span className="font-roboto text-[11px] font-medium text-emerald-400 tracking-wide uppercase">
            Admin Farm Management
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
        {navigation.map((item) => <NavItem key={item.name} item={item} farmId={currentFarmId} />)}
      </nav>

      {/* Footer - Minimalist Badge */}
      <div className="px-4 py-6">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50"></div>
          <span className="font-roboto text-xs font-medium text-slate-500 tracking-wider">
            Version 1.0
          </span>
        </div>
      </div>
    </div>
  )
}

function NavItem({ item, farmId }: { item: typeof navigation[0]; farmId: string | null }) {
  // Use useLocation for route matching instead of conditional useMatch calls
  const location = useLocation()
  const pathname = location.pathname

  // Determine if this nav item is active based on current path
  let isActive = false

  if (item.href === ROUTES.HOME) {
    // Home: exact match only
    isActive = pathname === item.href || pathname === '/'
  } else if (item.href === ROUTES.FARMS) {
    // Farms: match /farms or /farms/:farmId but NOT feature routes
    isActive = pathname === '/farms' || /^\/farms\/[^/]+$/.test(pathname)
  } else if (item.href === ROUTES.PONDS) {
    // Ponds: match ponds routes
    isActive = pathname === '/ponds' || /^\/farms\/[^/]+\/ponds(\/[^/]+)?$/.test(pathname)
  } else if (item.href === ROUTES.WATER_QUALITY) {
    // Water Quality: match both /water-quality and /water-quality/trends
    isActive = pathname === '/water-quality' || /^\/farms\/[^/]+\/water-quality(\/.*)?$/.test(pathname)
  } else {
    // Other feature routes (fish-health, maintenance, feed, ai, team, settings)
    const featurePath = item.farmFeature || item.href.replace('/', '')
    isActive = pathname === item.href ||
               (pathname.startsWith('/farms/') && pathname.includes(`/${featurePath}`))
  }

  // Determine the actual href to use
  // If we have a farmId and this is a farm-scoped feature, use farm-scoped URL
  const href = farmId && item.farmFeature
    ? `/farms/${farmId}/${item.farmFeature}`
    : item.href
  return (
    <Link
      to={href}
      className={cn(
        "font-roboto group flex items-center gap-3 rounded-xl px-4 py-3 text-[14px] transition-all duration-200 relative",
        isActive
          ? "bg-emerald-500/10 text-emerald-400 font-medium shadow-lg shadow-emerald-500/5"
          : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 font-normal"
      )}
    >
      {/* Active indicator bar */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-emerald-400 to-teal-500 rounded-r-full" />
      )}

      {/* Icon with color transition */}
      <div className={cn(
        "flex-shrink-0 transition-all duration-200",
        isActive
          ? "text-emerald-400 scale-110"
          : "text-slate-500 group-hover:text-slate-300 group-hover:scale-105"
      )}>
        {item.icon}
      </div>

      {/* Text with letter spacing */}
      <span className={cn(
        "flex-1 tracking-wide",
        isActive ? "text-white" : ""
      )}>
        {item.name}
      </span>

      {/* Subtle glow effect on active */}
      {isActive && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-500/5 to-transparent pointer-events-none" />
      )}
    </Link>
  )
}
